import ErrorState from '@pinecast/common/ErrorState';
import LoadingState from '@pinecast/common/LoadingState';
import ChromeSidebar, {
  ChromeSidebarItem,
} from '@pinecast/db-spa/App/ChromeSidebar';
import {PageTitleProvider} from '@pinecast/db-spa/App/usePageTitle';
import {useQuery} from '@pinecast/gql';
import {SubscriptionsPage} from '@pinecast/gql/__generated_tj__/SubscriptionsPage';
import {defineMessages, FormattedMessage} from '@pinecast/i18n';
import {Navigate} from 'react-router-dom';

import Chrome from '../Chrome';

import ReceiptList from './ReceiptList';
import SubscriptionPageHeader from './SubscriptionPageHeader';
import SubscriptionsPageRecurringDonationList from './SubscriptionsPageRecurringDonationList';
import {SUBSCRIPTIONS_PAGE} from './subscriptionsQuery';
import {PageHeader} from '@pinecast/common/Text';
import Section from '@pinecast/common/Section';

const messages = defineMessages({
  title: {
    id: 'tj-spa.SubscriptionsPage.title',
    description: 'Title of the page. {subtitle} is the current page',
    defaultMessage: '{subtitle} - Podcast Contributions',
  },
  receipts: {
    id: 'tj-spa.SubscriptionsPage.receipts',
    description: 'Receipts tab of the tip jar',
    defaultMessage: 'Receipts',
  },
  recurringDonations: {
    id: 'tj-spa.SubscriptionsPage.recurringDonations',
    description: 'Active tab of the tip jar',
    defaultMessage: 'Active subscriptions',
  },

  loading: {
    id: 'tj=spa.SubscriptionsPage.loading',
    description: 'Loading message for tip jar subscriptions',
    defaultMessage: 'Loading subscriptions…',
  },
  error: {
    id: 'tj=spa.SubscriptionsPage.error',
    description: 'Generic error message for tip jar subscriptions',
    defaultMessage: 'We could not load your subscriptions.',
  },
  errorCopy: {
    id: 'tj=spa.SubscriptionsPage.error.copy',
    description: 'Generic error message copy for tip jar subscriptions',
    defaultMessage:
      'We encountered a problem while trying to load your subscriptions.',
  },
  errorCta: {
    id: 'tj=spa.SubscriptionsPage.error.cta',
    description: 'Generic error message retry button for tip jar subscriptions',
    defaultMessage: 'Retry',
  },
});

const SubscriptionsPageWrapper = ({children}: {children: React.ReactNode}) => {
  return (
    <PageTitleProvider message={messages.title}>
      <Chrome>
        <SubscriptionPageHeader />
        <ChromeSidebar
          links={
            <>
              <ChromeSidebarItem
                exact={false}
                labelMessage={intl =>
                  intl.formatMessage(messages.recurringDonations)
                }
                path="/subscriptions/recurring_tips"
              />
              <ChromeSidebarItem
                labelMessage={intl => intl.formatMessage(messages.receipts)}
                path="/subscriptions/receipts"
              />
            </>
          }
        >
          {children}
        </ChromeSidebar>
      </Chrome>
    </PageTitleProvider>
  );
};

const useSubscriptionsPage = () => {
  const subscriptionsPage = useQuery<SubscriptionsPage>(SUBSCRIPTIONS_PAGE);

  const loadingState = subscriptionsPage.loading && (
    <LoadingState title={<FormattedMessage {...messages.loading} />} />
  );
  const errorState = subscriptionsPage.error && (
    <ErrorState
      title={<FormattedMessage {...messages.error} />}
      copy={<FormattedMessage {...messages.errorCopy} />}
      actionLabel={<FormattedMessage {...messages.errorCta} />}
      onAction={subscriptionsPage.refetch}
    />
  );
  return {
    loadingState,
    errorState,
    subscriptionsPage,
  };
};

export const SubscriptionsPageIndex = () => {
  const {loadingState, errorState, subscriptionsPage} = useSubscriptionsPage();

  return (
    <SubscriptionsPageWrapper>
      {loadingState ||
      errorState ||
      subscriptionsPage.data!.tipJarSubscriptions.length ? (
        <Navigate to="/subscriptions/recurring_tips" replace />
      ) : (
        <Navigate to="/subscriptions/receipts" replace />
      )}
    </SubscriptionsPageWrapper>
  );
};

export const SubscriptionsPageReceipts = () => {
  const {loadingState, errorState, subscriptionsPage} = useSubscriptionsPage();

  return (
    <SubscriptionsPageWrapper>
      <PageHeader>
        <FormattedMessage {...messages.receipts} />
      </PageHeader>
      <Section>
        {loadingState || errorState || (
          <ReceiptList events={subscriptionsPage.data!.tipJarTipEvents} />
        )}
      </Section>
    </SubscriptionsPageWrapper>
  );
};

export const SubscriptionsPageRecurringDonations = () => {
  const {loadingState, errorState, subscriptionsPage} = useSubscriptionsPage();

  return (
    <SubscriptionsPageWrapper>
      <PageHeader>
        <FormattedMessage {...messages.recurringDonations} />
      </PageHeader>
      <Section>
        {loadingState || errorState || (
          <SubscriptionsPageRecurringDonationList
            subscriptions={subscriptionsPage.data!.tipJarSubscriptions}
          />
        )}
      </Section>
    </SubscriptionsPageWrapper>
  );
};
